import { Fragment, useEffect, useState } from 'react';

import { Combobox } from '@headlessui/react';
import { IconZoomOutline } from '@iwoca/icons';
import {
  fetchGetPostcodeLookup,
  GetPostcodeLookupResponse,
} from '@iwoca/lapi-client/edge';
import { Input as OrionInput } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useField } from 'formik';
import { useFormContext, useWatch } from 'react-hook-form';

import { addressValidator } from './addressValidator';
import { InlineButton } from '../../Buyer/components/InlineButton/InlineButton';
import { extractError } from '../../utils/ReactHookForm';
import { HookFormInput, Input } from '../Input/Input';
import { InputError } from '../InputError/InputError';

type TResidentialAddress = GetPostcodeLookupResponse['data'][number];
type TResidentialAddressFormValues = {
  buildingNumber: string;
  buildingName: string;
  streetName: string;
  town: string;
  postcode: string;
};

export const AddressInput = ({
  address,
  disabled,
}: {
  address: TResidentialAddressFormValues;
  disabled?: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<TResidentialAddress[]>(
    [],
  );
  const [addressField, , { setValue: setAddressField }] = useField('address');
  const [isManualAddressField, , { setValue: setIsManualAddress }] =
    useField('address.manual');
  const [, { touched, error }] = useField('address.postcode');

  useEffect(() => {
    const postcodeSearch = setTimeout(async () => {
      if (
        addressValidator
          .pick(['postcode'])
          .isValidSync({ postcode: searchQuery })
      ) {
        try {
          const response = await fetchGetPostcodeLookup({
            query: { postcode: searchQuery },
          });
          setAddressOptions(response.data);
        } catch {
          setAddressOptions([]);
        }
      } else {
        setAddressOptions([]);
      }
    }, 200);

    return () => {
      clearTimeout(postcodeSearch);
    };
  }, [searchQuery]);

  return (
    <div>
      <Combobox
        value={addressField.value}
        disabled={disabled}
        onChange={async (address: TResidentialAddress) => {
          await setAddressField({
            buildingNumber: address.house_number,
            streetName: address.street_line_1,
            town: address.town,
            postcode: address.postcode,
            buildingName: address.house_name,
            equifaxToken: address.ptc_abs_code,
          });
          await setIsManualAddress(false);
        }}
      >
        <div className="relative flex max-w-[400px] flex-col">
          <div className="relative">
            <Combobox.Input
              onChange={(event) => {
                setSearchQuery(event.target.value);
              }}
              as={Fragment}
            >
              <OrionInput
                name="address.postcode"
                placeholder="Search postcode..."
                className="fs-mask"
                disabled={disabled}
                iconLeft={<IconZoomOutline />}
                label="Home postcode"
              />
            </Combobox.Input>
          </div>
          <InputError
            error={error}
            isVisible={!isManualAddressField.value && touched}
          />
          {searchQuery && (
            <Combobox.Options
              className="fs-mask absolute top-[82px] z-[2] m-0 max-h-[200px] w-full list-none overflow-y-auto rounded-s border border-divider bg-white p-0 text-grey-60"
              as="ul"
            >
              {addressOptions.length > 0 ? (
                addressOptions.map((address, index) => {
                  const addressName = [
                    address.house_name,
                    address.house_number,
                    address.street_line_1,
                  ]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <Combobox.Option
                      className={({ active }) =>
                        clsx(
                          'px-l py-s hover:cursor-pointer hover:bg-secondary-97',
                          {
                            'cursor-pointer bg-secondary-97': active,
                          },
                        )
                      }
                      key={index}
                      value={address}
                      as="li"
                    >
                      {addressName}
                    </Combobox.Option>
                  );
                })
              ) : (
                <p className="m-0 px-l py-s">No results</p>
              )}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
      {!isManualAddressField.value &&
        (address.postcode ? (
          <>
            <div className="fs-mask my-r">
              <div>
                {address.buildingName} {address.buildingNumber}{' '}
                {address.streetName}
              </div>
              <div>{address.town}</div>
              <div>{address.postcode}</div>
            </div>
            <InlineButton onClick={() => setIsManualAddress(true)}>
              Edit address
            </InlineButton>
          </>
        ) : (
          <InlineButton
            className="mx-0 mb-r mt-s !text-s"
            onClick={async () => {
              await setAddressField({
                ...addressField.value,
                equifaxToken: '',
              });
              await setIsManualAddress(!isManualAddressField.value);
            }}
          >
            Enter address manually
          </InlineButton>
        ))}

      {isManualAddressField.value && (
        <div className="mt-xl max-w-[400px]">
          <Input
            label="Building number"
            name="address.buildingNumber"
            className="mb-r"
            inputMode="numeric"
          />
          <Input
            label="Street name"
            name="address.streetName"
            className="mb-r"
            inputMode="text"
          />
          <Input
            label="Town"
            name="address.town"
            className="mb-r"
            inputMode="text"
          />
          <Input label="Postcode" name="address.postcode" inputMode="text" />
        </div>
      )}
    </div>
  );
};

export const HookFormAddressInput = ({
  address,
  disabled,
}: {
  address: TResidentialAddressFormValues;
  disabled?: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<TResidentialAddress[]>(
    [],
  );
  const { control, setValue, formState, getFieldState } = useFormContext();
  const addressValue = useWatch({ control, name: 'address' });
  const isManualAddress = useWatch({ control, name: 'address.manual' });
  const postcodeFieldState = getFieldState('address.postcode', formState);
  const postcodeError = extractError(postcodeFieldState.error);
  const isPostcodeTouched =
    postcodeFieldState.isTouched || formState.isSubmitted;

  useEffect(() => {
    const postcodeSearch = setTimeout(async () => {
      if (
        addressValidator
          .pick(['postcode'])
          .isValidSync({ postcode: searchQuery })
      ) {
        try {
          const response = await fetchGetPostcodeLookup({
            query: { postcode: searchQuery },
          });
          setAddressOptions(response.data);
        } catch {
          setAddressOptions([]);
        }
      } else {
        setAddressOptions([]);
      }
    }, 200);

    return () => {
      clearTimeout(postcodeSearch);
    };
  }, [searchQuery]);

  return (
    <div>
      <Combobox
        value={addressValue}
        disabled={disabled}
        onChange={(address: TResidentialAddress) => {
          setValue(
            'address',
            {
              buildingNumber: address.house_number,
              streetName: address.street_line_1,
              town: address.town,
              postcode: address.postcode,
              buildingName: address.house_name,
              equifaxToken: address.ptc_abs_code,
            },
            { shouldValidate: true, shouldDirty: true, shouldTouch: true },
          );
          setValue('address.manual', false);
        }}
      >
        <div className="relative flex max-w-[400px] flex-col">
          <div className="relative">
            <Combobox.Input
              onChange={(event) => {
                setSearchQuery(event.target.value);
              }}
              as={Fragment}
            >
              <OrionInput
                name="address.postcode"
                placeholder="Search postcode..."
                className="fs-mask"
                disabled={disabled}
                iconLeft={<IconZoomOutline />}
                label="Home postcode"
              />
            </Combobox.Input>
          </div>
          <InputError
            error={postcodeError}
            isVisible={!isManualAddress && isPostcodeTouched}
          />
          {searchQuery && (
            <Combobox.Options
              className="fs-mask absolute top-[82px] z-[2] m-0 max-h-[200px] w-full list-none overflow-y-auto rounded-s border border-divider bg-white p-0 text-grey-60"
              as="ul"
            >
              {addressOptions.length > 0 ? (
                addressOptions.map((address, index) => {
                  const addressName = [
                    address.house_name,
                    address.house_number,
                    address.street_line_1,
                  ]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <Combobox.Option
                      className={({ active }) =>
                        clsx(
                          'px-l py-s hover:cursor-pointer hover:bg-secondary-97',
                          {
                            'cursor-pointer bg-secondary-97': active,
                          },
                        )
                      }
                      key={index}
                      value={address}
                      as="li"
                    >
                      {addressName}
                    </Combobox.Option>
                  );
                })
              ) : (
                <p className="m-0 px-l py-s">No results</p>
              )}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
      {!isManualAddress &&
        (address.postcode ? (
          <>
            <div className="fs-mask my-r">
              <div>
                {address.buildingName} {address.buildingNumber}{' '}
                {address.streetName}
              </div>
              <div>{address.town}</div>
              <div>{address.postcode}</div>
            </div>
            <InlineButton onClick={() => setValue('address.manual', true)}>
              Edit address
            </InlineButton>
          </>
        ) : (
          <InlineButton
            className="mx-0 mb-r mt-s !text-s"
            onClick={() => {
              setValue('address', {
                ...addressValue,
                equifaxToken: '',
              });
              setValue('address.manual', !isManualAddress);
            }}
          >
            Enter address manually
          </InlineButton>
        ))}

      {isManualAddress && (
        <div className="mt-xl max-w-[400px]">
          <HookFormInput
            label="Building number"
            name="address.buildingNumber"
            className="mb-r"
            inputMode="numeric"
          />
          <HookFormInput
            label="Street name"
            name="address.streetName"
            className="mb-r"
            inputMode="text"
          />
          <HookFormInput
            label="Town"
            name="address.town"
            className="mb-r"
            inputMode="text"
          />
          <HookFormInput
            label="Postcode"
            name="address.postcode"
            inputMode="text"
          />
        </div>
      )}
    </div>
  );
};
